import ImageCarouselComponent from './ImageCarouselComponent'
import * as Dialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { useEffect, useRef, useState } from 'react'
import { Cross2Icon } from '@radix-ui/react-icons'
import { useTranslation } from 'react-i18next'
import styles from './ImageCarousel.module.scss'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { MEDIA_TABLET_MIN } from '../../lib/breakpoints'

interface ImageCarouselProps {
  featuredAssetSource?: string
  enableOverlayModal?: boolean
  showBadges?: boolean
  maxVisibleThumbnails?: number
}

const SHADOW_HOST_Z_INDEX = '2147483647'

const ImageCarousel = ({
  featuredAssetSource,
  enableOverlayModal,
  showBadges = true,
  maxVisibleThumbnails,
}: ImageCarouselProps) => {
  const { t } = useTranslation()
  const [isOverlayOpen, setIsOverlayOpen] = useState(false)
  const [activeSlide, setActiveSlide] = useState(0)
  const portalContainerRef = useRef<HTMLDivElement>(null)

  const isTabletOrLarger = useMediaQuery(MEDIA_TABLET_MIN)
  const showOverlayModal = enableOverlayModal && isTabletOrLarger

  const handleImageClick = (activeSlide: number) => {
    if (!showOverlayModal) {
      return
    }
    setIsOverlayOpen(true)
    setActiveSlide(activeSlide)
  }

  useEffect(() => {
    if (!showOverlayModal) {
      setIsOverlayOpen(false)
    }

    const root = portalContainerRef.current?.getRootNode()
    if (!(root instanceof ShadowRoot)) {
      return
    }
    const el = root.host as HTMLElement
    el.style.zIndex = isOverlayOpen && showOverlayModal ? SHADOW_HOST_Z_INDEX : ''
    return () => {
      el.style.zIndex = ''
    }
  }, [isOverlayOpen, showOverlayModal])

  return (
    <div>
      <ImageCarouselComponent
        featuredAssetSource={featuredAssetSource}
        onImageClick={handleImageClick}
        showBadges={showBadges}
        maxVisibleThumbnails={maxVisibleThumbnails}
      />

      {showOverlayModal && (
        <Dialog.Root open={isOverlayOpen} onOpenChange={(open) => setIsOverlayOpen(open)}>
          <div ref={portalContainerRef} />
          <Dialog.Portal container={portalContainerRef.current ?? undefined}>
            <Dialog.Overlay className={styles.overlayBackdrop} />
            <Dialog.Content className={styles.overlayModal} aria-describedby={undefined}>
              <VisuallyHidden>
                <Dialog.Title>{t('imageCarousel.dialogTitle')}</Dialog.Title>
              </VisuallyHidden>
              <div className={styles.overlayBody}>
                <div className={styles.overlayHeader}>
                  <button
                    type="button"
                    onClick={() => setIsOverlayOpen(false)}
                    aria-label={t('a11y.imageCarousel.aria.closeGallery')}
                    className={styles.overlayCloseButton}
                  >
                    <Cross2Icon width={32} height={32} aria-hidden />
                  </button>
                </div>
                <ImageCarouselComponent
                  featuredAssetSource={featuredAssetSource}
                  activeSlide={activeSlide}
                  onActiveSlideChange={setActiveSlide}
                  useMeasuredWidth={false}
                  showArrows
                  showBadges={showBadges}
                  maxVisibleThumbnails={maxVisibleThumbnails}
                />
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  )
}

export default ImageCarousel
